import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectedStore } from '@web/app/shared';
import { ChevronDown, User } from 'lucide-react';

import { useOwnerStore } from '@codes/owner';
import { useStorageTransfer } from '@codes/storage-transfer';
import { useTheme } from '@codes/theme';
import { Avatar, AvatarFallback, AvatarImage, SharedSpinner, cn } from '@codes/ui-kit';
import { useLogout, useWebCoreStore } from '@codes/web-core';

import { ContactModal } from '../../../ContactModal';

export const UserBadge = () => {
    const [opened, setOpened] = useState(false);
    const { userName, profile } = useWebCoreStore();
    const { t, i18n } = useTranslation();
    const { theme, setTheme } = useTheme();
    const queryClient = useQueryClient();
    const ownerInfo = useOwnerStore(state => state.owner);
    const navigate = useNavigate();
    const [showContactModal, setShowContactModal] = useState(false);

    const target = import.meta.env.VITE_BOX_HOST?.toLowerCase();
    const { handleTransfer } = useStorageTransfer(target as string);

    const { clearSelection } = useSelectedStore();

    const [language, setLanguage] = useState<string>(i18n.language || 'en');

    const handleThemeChange = () => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    };
    const toggleLanguage = () => {
        const newLanguage = language === 'en' ? 'ko' : 'en';
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
    };

    const logoutMutation = useLogout(async () => {
        toast({
            title: t('auth.successLogout'),
            duration: 2000,
        });

        await queryClient.resetQueries();
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    });

    const onTransferClick = async () => {
        const result = await handleTransfer();
        if (result.success) {
            console.log('전송 성공!');
        } else {
            console.error('전송 실패:', result.error);
        }
    };

    if (!ownerInfo) {
        return <SharedSpinner />;
    }

    return (
        <>
            <DropdownMenu open={opened} onOpenChange={setOpened}>
                <DropdownMenuTrigger className="focus-visible:outline-0 flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-hover transition-colors duration-150">
                    <Avatar className="w-7 h-7 border-0 bg-accent p-0">
                        <AvatarImage src={profile?.$user?.photo} alt="@shadcn" />
                        <AvatarFallback className="bg-accent">
                            <User className="w-4 h-4 text-point" />
                        </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium max-md:hidden">{userName}</span>
                    <ChevronDown
                        className={cn(
                            'w-4 h-4 text-text-500 transition-transform duration-200 max-md:hidden',
                            opened && 'rotate-180'
                        )}
                    />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[240px] p-3">
                    <div className="mb-2">
                        <div className="text-sm font-semibold">{userName}</div>
                        {!!ownerInfo?.email && <div className="text-xs text-text-500 mt-0.5">{ownerInfo.email}</div>}
                    </div>
                    <DropdownMenuSeparator className="-mx-3 bg-border" />
                    <DropdownMenuLabel className="text-xs text-text-500 font-medium py-0 mb-1.5 mt-2">
                        MY
                    </DropdownMenuLabel>
                    <div className="flex flex-col gap-0.5 mb-1">
                        <DropdownMenuItem
                            className="text-sm font-normal py-1.5 px-2 rounded-md cursor-pointer"
                            onClick={() => navigate('/my-page?tab=info')}
                        >
                            {t('header.personalInfo')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-sm font-normal py-1.5 px-2 rounded-md cursor-pointer"
                            onClick={() => navigate('/my-page?tab=payment')}
                        >
                            {t('header.billing')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            className="text-sm font-normal py-1.5 px-2 rounded-md cursor-pointer"
                            onClick={() => navigate('/my-page?tab=subscriptions')}
                        >
                            {t('header.subscriptions')}
                        </DropdownMenuItem>
                    </div>
                    <DropdownMenuSeparator className="-mx-3 bg-border" />
                    <DropdownMenuItem
                        className="text-sm font-normal py-1.5 px-2 rounded-md cursor-pointer mt-1"
                        onClick={() => setShowContactModal(true)}
                    >
                        Contact Us
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        className="text-sm font-normal py-1.5 px-2 rounded-md cursor-pointer text-error"
                        onClick={() => logoutMutation.mutate()}
                    >
                        {t('header.logout')}
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
        </>
    );
};
